/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  Dialog,
  Paper,
} from '@mui/material';
import {
  getFieldProps,
  InputPhone,
  getFormProps,
  Validate,
  useForm,
  getSubmitButtonProps,
  TimezoneSelector,
} from '@marageti/z4-lib';
import { Controller } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReactComponent as ZicassoSVG } from '../../assets/svg/zicasso-logo-redesign.svg';
import { ConfirmPhoneViewProps } from './types';
import './verification.scss';

const schema = z.object({
  phone: z.object({
    prefix: z.string(),
    number: z.string(),
  }).refine((value) => {
    if (!value.number) return true;
    return Validate.phone(`${value.prefix}${value.number}`);
  }, { message: 'Please enter a valid number.' }),
  timeZone: z.object({
    name: z.string(),
    rawOffsetInMinutes: z.any(),
  })
    .refine((data) => data.name && data.rawOffsetInMinutes !== undefined, {
      message: 'Required',
      path: [],
    }),
});

const ConfirmPhoneContent = ({
  defaultValues,
  onSubmit,
}: ConfirmPhoneViewProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const {
    handleSubmit,
    formState: {
      errors, isSubmitting, isValid,
    },
    control,
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);
  return (
    <Stack
      spacing={3}
      sx={{
        width: isDesktop ? '100%' : '342px',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Stack flexDirection="column" spacing={4} alignItems="center">
        <Stack spacing={4} alignItems="center">
          <ZicassoSVG aria-label="Zicasso" />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ pb: 2 }}>
              Confirm Your Phone Number
            </Typography>
            <Typography color="text.secondary" style={{ textAlign: 'center' }}>
              Our top travel specialists are in high demand and prioritize travelers who provide a contact number. We respect and protect your privacy.
            </Typography>
          </Box>
        </Stack>
        <Stack component="form" {...getFormProps(handleSubmit(onSubmit))} sx={{ width: '100%' }}>
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <InputPhone
                id="phone"
                label="Phone Number"
                {...field}
                {...getFieldProps('phone', errors)}
              />
            )}
          />

          <Controller
            name="timeZone"
            control={control}
            render={({ field }) => (
              <TimezoneSelector
                required
                id="timeZone-selector"
                label="Timezone"
                {...getFieldProps('timeZone', errors)}
                {...field}
              />
            )}
          />

          <LoadingButton
            variant="contained"
            type="submit"
            sx={{ mt: 3 }}
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            Confirm
          </LoadingButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

const ConfirmPhoneView = ({
  defaultValues,
  onSubmit,
}: ConfirmPhoneViewProps): React.ReactElement => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isDesktop ? (
        <Dialog
          open
          aria-labelledby="verification-modal-title"
          aria-describedby="verification-modal-description"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              width: '390px',
              maxHeight: '95vh',
              overflowY: 'auto',
              borderRadius: '16px',
              position: 'relative',
              zIndex: theme.zIndex.modal,
            }}
          >
            <ConfirmPhoneContent
              defaultValues={defaultValues}
              onSubmit={onSubmit}
            />
          </Paper>
        </Dialog>
      ) : (
        <ConfirmPhoneContent
          defaultValues={defaultValues}
          onSubmit={onSubmit}
        />
      )}
    </Box>
  );
};

export default ConfirmPhoneView;
